import { type ComponentProps, type FC, useEffect, useState } from 'react';
import { Player, PlayerEvent } from '@lottiefiles/react-lottie-player';
import { useReducedMotion } from '@react-spring/web';
import { isAutomatedBrowser } from 'src/lib/browser-utils';

// lottie-web renders every animation on its own `requestAnimationFrame` loop, so
// a page full of Lotties repaints at the display refresh rate (120Hz on a
// ProMotion Mac). These are ambient marketing animations — 30FPS is
// indistinguishable and roughly halves their main-thread cost.
const DEFAULT_FPS = 30;

type LottieAnimation = Parameters<
  NonNullable<ComponentProps<typeof Player>['lottieRef']>
>[0];

// Lottie animates via JS (lottie-web), so `prefers-reduced-motion` and Happo's
// CSS animation freezing can't pause it — render a static frame 0 instead.
const LottiePlayer: FC<ComponentProps<typeof Player> & { fps?: number }> = ({
  autoplay,
  fps = DEFAULT_FPS,
  loop,
  onEvent,
  ...rest
}) => {
  const freeze = useReducedMotion() || isAutomatedBrowser();
  // lottie-web fetches the animation JSON at runtime, so a capture can race the
  // load and snapshot an empty box. Lottie paints frame 0 synchronously on
  // Load, strictly before React clears the marker attribute below.
  const [loading, setLoading] = useState(true);
  // Holding the instance in state (rather than a ref) restarts the loop below
  // whenever Player rebuilds the animation, e.g. when `src` changes.
  const [animation, setAnimation] = useState<LottieAnimation | null>(null);
  const shouldAnimate = Boolean(autoplay) && !freeze;

  useEffect(() => {
    if (!animation || !shouldAnimate) return;

    const frameDuration = 1000 / fps;
    let rafId = 0;
    let lastRenderedAt = performance.now();
    let elapsedSeconds = 0;

    // Advancing by wall-clock time rather than by a fixed step keeps playback at
    // the composition's real duration no matter what rate we render at.
    const renderNextFrame = () => {
      rafId = requestAnimationFrame(renderNextFrame);
      // Player destroys the instance the moment `src` changes and only rebuilds
      // it after fetching the new JSON, so the one held here can already be
      // dead. lottie-web nulls `renderer` on destroy and then no-ops every call,
      // which would leave this loop running against nothing. Stand down and let
      // the next `lottieRef` restart the effect with the replacement.
      if (!animation.renderer) {
        cancelAnimationFrame(rafId);
        return;
      }

      const now = performance.now();
      const sinceLastRender = now - lastRenderedAt;
      if (sinceLastRender < frameDuration) return;

      lastRenderedAt = now;
      const { frameRate, totalFrames } = animation;
      if (!totalFrames) return;

      elapsedSeconds += sinceLastRender / 1000;
      const frame = elapsedSeconds * frameRate;

      if (!loop && frame >= totalFrames) {
        animation.goToAndStop(totalFrames - 1, true);
        cancelAnimationFrame(rafId);
        return;
      }

      animation.goToAndStop(frame % totalFrames, true);
    };

    rafId = requestAnimationFrame(renderNextFrame);
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [animation, fps, loop, shouldAnimate]);

  // Snap back to frame 0 when playback is off, so a mid-session switch to
  // reduced motion lands on the same static frame a frozen load would.
  useEffect(() => {
    if (animation && !shouldAnimate) {
      animation.goToAndStop(0, true);
    }
  }, [animation, shouldAnimate]);

  return (
    // `display: contents` keeps the marker out of layout (Player accepts no
    // data-* props). `data-lottie-loading`: Happo waits for these to clear
    // before capture (.storybook/lib/happo.ts).
    <span
      data-lottie-loading={loading ? '' : undefined}
      style={{ display: 'contents' }}
    >
      <Player
        {...rest}
        // Playback is driven by the throttled loop above, never by lottie-web.
        autoplay={false}
        loop={loop}
        lottieRef={setAnimation}
        onEvent={(event) => {
          if (event === PlayerEvent.Load) {
            setLoading(false);
          }
          onEvent?.(event);
        }}
      />
    </span>
  );
};

export default LottiePlayer;
